import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import { PIPELINE_STAGE_STATE } from "contracts/enums/run-stage";
import type { TovSourceStatus } from "contracts/enums/tov";
import { TOV_PARSE_STATUS, TOV_SOURCE_STATUS } from "contracts/enums/tov";
import type { PipelineStep } from "../../ui/PipelineStepper";

// Stage view of the TOV cascade, derived from the single `status` column.
//
// The status is an ordinal, so how far a source got is knowable, but WHERE a
// failure happened is not: `failed` is one bucket for parse, vectorize and map
// failures alike, and `embedded` doubles as "map requested, in flight". So a
// failed source is reported as having failed *after* its last proven stage
// rather than being assigned to a stage it cannot be shown to have reached.
// The honest fix is per-stage job rows in the DB, not a better guess here.

const STAGE_ORDER = [
  { key: "upload", label: "Upload" },
  { key: "parse", label: "Parse" },
  { key: "chunk", label: "Chunk" },
  { key: "embed", label: "Embed" },
  { key: "map", label: "Map" },
] as const;

type TovStageKey = (typeof STAGE_ORDER)[number]["key"];

/**
 * Per status: the last stage it proves complete, and the stage it is working
 * on. One entry per status rather than two parallel maps keyed by array index,
 * so a stage cannot be added to the order without the compiler demanding both
 * halves for every status.
 */
const STATUS_PROGRESS: Record<
  TovSourceStatus,
  { completedThrough: TovStageKey | null; active: TovStageKey | null }
> = {
  created: { completedThrough: null, active: null },
  uploaded: { completedThrough: "upload", active: "parse" },
  parse_requested: { completedThrough: "upload", active: "parse" },
  parsing: { completedThrough: "upload", active: "parse" },
  parsed: { completedThrough: "parse", active: "chunk" },
  chunked: { completedThrough: "chunk", active: "embed" },
  embedded: { completedThrough: "embed", active: "map" },
  mapped: { completedThrough: "map", active: null },
  // A failed source keeps no record of how far it got; callers pair this with
  // the parse-job error to say something specific.
  failed: { completedThrough: null, active: null },
  superseded: { completedThrough: "map", active: null },
};

function indexOf(key: TovStageKey | null): number {
  return key === null
    ? -1
    : STAGE_ORDER.findIndex((stage) => stage.key === key);
}

export function sourceStages(status: TovSourceStatus): PipelineStep[] {
  const progress = STATUS_PROGRESS[status];
  const completedThrough = indexOf(progress.completedThrough);
  const active = indexOf(progress.active);
  return STAGE_ORDER.map((stage, index) => ({
    key: stage.key,
    label: stage.label,
    state:
      index <= completedThrough
        ? PIPELINE_STAGE_STATE.DONE
        : index === active
          ? PIPELINE_STAGE_STATE.ACTIVE
          : PIPELINE_STAGE_STATE.WAITING,
  }));
}

/**
 * What actually failed, in the user's terms. Prefers the parse job's own error
 * (the one stage with a job row and a real message) and falls back to the
 * source's free-text failure reason, which is all a map failure leaves behind.
 */
export function sourceFailureDetail(
  source: TovSourceResponse,
): { stage: string; message: string } | null {
  if (source.status !== TOV_SOURCE_STATUS.FAILED) return null;
  const failedParse = source.parseJobs.find(
    (job) => job.status === TOV_PARSE_STATUS.FAILED && job.errorMessage,
  );
  if (failedParse?.errorMessage) {
    return { stage: "Parse", message: failedParse.errorMessage };
  }
  return {
    // Every non-parse failure path runs after embedding (the mapper is the
    // only other writer of `failed`), so this is a statement of fact, not a
    // guess at which stage broke.
    stage: "After embedding",
    message: source.failureReason ?? "No detail was recorded.",
  };
}
